import { supabase } from '@/lib/supabase';
import { toUserMessage } from '@/lib/errors';
import type { Tables } from '@/types/database';
import { ok, err, type ApiResponse } from './_base';

export type SentEmail = Tables<'sent_emails'>;

export interface SendEmailAttachment {
  filename: string;
  /** base64 — sin prefijo "data:" */
  content: string;
  content_type?: string;
}

export interface SendEmailPayload {
  empresa_id: string;
  to: string | string[];
  subject: string;
  html: string;
  plantilla:
    | 'comprobante'
    | 'recordatorio'
    | 'recordatorio_1'
    | 'recordatorio_2'
    | 'recordatorio_3'
    | 'notificacion'
    | 'test'
    | 'cta_cte_reclamo'
    | 'intimacion'
    | 'escalado_legal';
  reply_to?: string;
  attachments?: SendEmailAttachment[];
  comprobante_id?: string;
  edificio_id?: string;
  administracion_id?: string;
  /** 0142 · si se pasa, programa para esa fecha-hora futura (override del throttle). */
  scheduled_at_override?: string;
}

export interface SendEmailResult {
  /** ID del job en email_queue. */
  job_id: string;
  /** Cuándo va a salir el mail (con throttle de 5 min global). */
  scheduled_at: string;
  estado: 'queued';
}

/**
 * TODO-MDC-16: encola un email individual respetando el piso global
 * de 5 min entre envíos por empresa. NO invoca edge fn directo —
 * crea un job en `email_queue` y deja que el cron lo procese.
 *
 * Por qué encolado y no inmediato: si el usuario manda factura por
 * error → reenvía → manda cta cte → manda recordatorio en 30 segundos,
 * el cliente recibe 4 mails. Spam total. La cola con piso 5 min
 * imposibilita esa cascada por cualquier path (lote o individual).
 *
 * El returned `scheduled_at` permite a la UI mostrar "Tu mail saldrá
 * a las HH:MM (en X minutos)" en vez de "Enviado".
 */
export async function sendEmail(
  payload: SendEmailPayload,
): Promise<ApiResponse<SendEmailResult>> {
  const toRaw = Array.isArray(payload.to) ? payload.to[0] : payload.to;
  if (!toRaw) return err('VALIDATION', 'Falta destinatario');
  const ccEmails = Array.isArray(payload.to) ? payload.to.slice(1) : [];

  const attachmentsJson = payload.attachments
    ? payload.attachments.map((a) => ({
        filename: a.filename,
        content: a.content,
        content_type: a.content_type ?? 'application/octet-stream',
      }))
    : undefined;

  const { data, error } = await supabase.rpc('enqueue_email_individual', {
    p_empresa_id: payload.empresa_id,
    p_to_email: toRaw,
    p_subject: payload.subject,
    p_html_body: payload.html,
    p_plantilla_tipo: payload.plantilla,
    p_reply_to: payload.reply_to ?? undefined,
    p_attachments: attachmentsJson,
    p_cc_emails: ccEmails.length > 0 ? ccEmails : undefined,
    p_comprobante_id: payload.comprobante_id ?? undefined,
    p_edificio_id: payload.edificio_id ?? undefined,
    p_administracion_id: payload.administracion_id ?? undefined,
    p_scheduled_at_override: payload.scheduled_at_override ?? undefined,
  });
  if (error) return err(error.code ?? 'ENQUEUE_FAIL', toUserMessage(error));
  const row = (data ?? [])[0];
  if (!row) return err('ENQUEUE_EMPTY', 'No se pudo encolar el email.');
  return ok({
    job_id: String(row.job_id),
    scheduled_at: String(row.scheduled_at),
    estado: 'queued',
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Listado
// ═══════════════════════════════════════════════════════════════════════════

export interface ListSentEmailsFilters {
  empresaId: string;
  estado?: 'queued' | 'sent' | 'failed' | 'delivered' | 'bounced' | 'complained';
  plantilla?: string;
  comprobanteId?: string;
  limit?: number;
}

export async function listSentEmails(
  filters: ListSentEmailsFilters,
): Promise<ApiResponse<SentEmail[]>> {
  let q = supabase
    .from('sent_emails')
    .select('*')
    .eq('empresa_id', filters.empresaId)
    .order('created_at', { ascending: false })
    .limit(filters.limit ?? 50);
  if (filters.estado) q = q.eq('estado', filters.estado);
  if (filters.plantilla) q = q.eq('plantilla', filters.plantilla);
  if (filters.comprobanteId) q = q.eq('comprobante_id', filters.comprobanteId);

  const { data, error } = await q;
  if (error) return err(error.code ?? 'LIST_FAILED', toUserMessage(error));
  return ok(data ?? []);
}

// ═══════════════════════════════════════════════════════════════════════════
// TODO-MDC-32: Lista unificada de envíos (sent_emails + email_queue.pending)
// ═══════════════════════════════════════════════════════════════════════════

export type EnvioUnifiedSource = 'sent' | 'queue';

export interface EnvioUnified {
  source: EnvioUnifiedSource;
  row_id: string;
  job_id: string | null;
  fecha: string;
  to_email: string;
  cc_emails: string[] | null;
  asunto: string;
  plantilla: string;
  estado: string;
  resend_id: string | null;
  error_msg: string | null;
  // sent_emails
  importe_total: number | null;
  zip_attached: boolean | null;
  attachments_filenames: string[] | null;
  zip_size_bytes: number | null;
  delivered_at: string | null;
  bounced_at: string | null;
  complained_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  last_event_at: string | null;
  events: Array<{ type: string; at: string; data?: Record<string, unknown> }> | null;
  edificio_id: string | null;
  administracion_id: string | null;
  comprobante_id: string | null;
  // email_queue
  scheduled_at: string | null;
  comprobantes_count: number | null;
  parte: number | null;
  partes_total: number | null;
  attempts: number | null;
  max_attempts: number | null;
  lote_id: string | null;
  // enriquecido
  edificio_nombre: string | null;
  administracion_nombre: string | null;
  administracion_codigo: string | null;
}

export async function listEnviosUnified(params: {
  empresaId: string;
  includePending?: boolean;
  limit?: number;
}): Promise<ApiResponse<EnvioUnified[]>> {
  // TODO-MDC-32: la RPC list_envios_unified es nueva y todavía no está
  // en los types generados. Cast a `never` para que el SDK no intente
  // resolver el shape de los args desde los Database types y rompa con
  // "Cannot read properties of undefined (reading 'rest')".
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.rpc as any)('list_envios_unified', {
    p_empresa_id: params.empresaId,
    p_include_pending: params.includePending ?? false,
    p_limit: params.limit ?? 200,
  });
  if (error)
    return err(
      (error as { code?: string }).code ?? 'LIST_FAILED',
      toUserMessage(error as Error),
    );
  return ok((data ?? []) as unknown as EnvioUnified[]);
}

// ═══════════════════════════════════════════════════════════════════════════
// Contador de fallos — para campanita en topbar
// ═══════════════════════════════════════════════════════════════════════════
export async function countFailedEmails(empresaId: string): Promise<ApiResponse<number>> {
  const { count, error } = await supabase
    .from('sent_emails')
    .select('id', { count: 'exact', head: true })
    .eq('empresa_id', empresaId)
    .in('estado', ['failed', 'bounced', 'complained']);
  if (error) return err(error.code ?? 'COUNT_FAILED', toUserMessage(error));
  return ok(count ?? 0);
}

// ═══════════════════════════════════════════════════════════════════════════
// Helpers de plantillas HTML
// ═══════════════════════════════════════════════════════════════════════════

interface ComprobanteEmailParams {
  empresaNombre: string;
  edificioNombre: string;
  tipo: string;
  numero: string;
  periodoLabel: string;
  totalFormateado: string;
  caeVencimiento: string | null;
  saludoNombre?: string;
  replyTo?: string;
}

export function buildComprobanteEmailHtml(p: ComprobanteEmailParams): {
  subject: string;
  html: string;
} {
  const subject = `${p.tipo} ${p.numero} · ${p.edificioNombre} · ${p.periodoLabel}`;
  const hola = p.saludoNombre ? `Hola ${p.saludoNombre}` : 'Hola';
  const html = `<!DOCTYPE html>
<html lang="es-AR">
<head><meta charset="utf-8"></head>
<body style="margin:0;padding:0;background:#f5f6f8;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f5f6f8;padding:24px 0;">
    <tr><td align="center">
      <table width="560" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:12px;box-shadow:0 1px 3px rgba(0,0,0,0.06);overflow:hidden;">
        <tr><td style="padding:28px 32px 20px 32px;border-bottom:1px solid #eef0f3;">
          <p style="margin:0;font-size:10px;letter-spacing:1.5px;text-transform:uppercase;color:#6b7280;font-weight:600;">${p.empresaNombre}</p>
          <h1 style="margin:6px 0 0 0;font-size:20px;color:#0f172a;font-weight:800;">${p.tipo} ${p.numero}</h1>
        </td></tr>
        <tr><td style="padding:24px 32px;color:#0f172a;font-size:14px;line-height:1.6;">
          <p style="margin:0 0 16px 0;">${hola},</p>
          <p style="margin:0 0 16px 0;">Te enviamos el comprobante correspondiente a <strong>${p.edificioNombre}</strong> por el período <strong>${p.periodoLabel}</strong>.</p>
          <table width="100%" cellpadding="0" cellspacing="0" style="margin:16px 0;border:1px solid #e5e7eb;border-radius:8px;">
            <tr><td style="padding:12px 16px;border-bottom:1px solid #e5e7eb;"><span style="color:#6b7280;font-size:12px;">Total</span><br><strong style="font-size:20px;color:#0f172a;">$ ${p.totalFormateado}</strong></td></tr>
            ${
              p.caeVencimiento
                ? `<tr><td style="padding:12px 16px;"><span style="color:#6b7280;font-size:12px;">CAE vence</span><br><strong style="font-size:14px;color:#0f172a;">${p.caeVencimiento}</strong></td></tr>`
                : ''
            }
          </table>
          <p style="margin:0 0 12px 0;">Encontrás el detalle completo en el archivo adjunto.</p>
          <p style="margin:0;color:#6b7280;font-size:12px;">Ante cualquier consulta podés responder este email${p.replyTo ? ` o escribirnos a ${p.replyTo}` : ''}.</p>
        </td></tr>
        <tr><td style="padding:16px 32px;background:#f9fafb;border-top:1px solid #eef0f3;font-size:11px;color:#6b7280;text-align:center;">
          Enviado por <strong>${p.empresaNombre}</strong> · via MANAXER
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
  return { subject, html };
}
